import { useState, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, Button, Row, Col, Spin, message, Radio } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import PropTypes from 'prop-types';
import { getUnavaliableDays, getBlockedDays, getExtensionPricing } from '../../../../../data/API';

const ExtendRentalModal = ({
  open,
  onClose,
  selectedOrder,
  selectedOrderDetails,
  loadingOrderDetails,
  customer,
  onOpenSummary
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extensionType, setExtensionType] = useState('weekly');
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]);
  const [showDateAdjustmentMessage, setShowDateAdjustmentMessage] = useState(false);
  const [originalCalculatedDate, setOriginalCalculatedDate] = useState(null);
  const [extensionPricingData, setExtensionPricingData] = useState(null);

  useEffect(() => {
    if (open && selectedOrderDetails) {
      initializeForm();
      fetchBlockedDays();
      fetchUnavailableDates();
      fetchExtensionPricing();
    }
  }, [open, selectedOrderDetails]);

  const fetchExtensionPricing = async () => {
    try {
      const response = await getExtensionPricing();
      if (response?.data?.status === 'success') {
        setExtensionPricingData(response.data.data);
      }
    } catch (error) {
      console.warn('Extension pricing API not available, using fallback values:', error);
    }
  };


  const fetchBlockedDays = async () => {
    try {
      const response = await getBlockedDays();
      const blockedDaysArray = response?.data?.results?.map((obj) => obj.day) || [];
      setBlockedDays(blockedDaysArray);
    } catch (error) {
      console.error('Error fetching blocked days:', error);
    }
  };

  const fetchUnavailableDates = async () => {
    try {
      let allDates = [];
      let currentPage = 1;
      let hasNextPage = true;

      while (hasNextPage) {
        const response = await getUnavaliableDays(currentPage);

        if (response.data.results) {
          allDates = [...allDates, ...response.data.results];
        }

        if (response.data.next) {
          currentPage++;
        } else {
          hasNextPage = false;
        }
      }

      setUnavailableDates(allDates);
    } catch (error) {
      console.error('Error fetching unavailable dates:', error);
    }
  };

  const isDateBlocked = (date) => {
    const checkDate = dayjs(date);

    const dayName = checkDate.format('dddd');
    if (blockedDays.includes(dayName)) {
      return true;
    }

    const dateString = checkDate.format('YYYY-MM-DD');
    if (unavailableDates.some(dateObj => dayjs(dateObj.date).format('YYYY-MM-DD') === dateString)) {
      return true;
    }

    return false;
  };

  const findNextAvailableDate = (startDate) => {
    let checkDate = dayjs(startDate);
    let attempts = 0;
    const maxAttempts = 30;
    while (attempts < maxAttempts) {
      if (!isDateBlocked(checkDate)) {
        return checkDate;
      }
      checkDate = checkDate.add(1, 'day');
      attempts++;
    }

    return checkDate;
  };

  const initializeForm = () => {
    const pickupDate = selectedOrderDetails?.pickup_date;
    const deliveryDate = selectedOrderDetails?.delivery_date || selectedOrderDetails?.delivery_details?.delivery_date;

    form.setFieldsValue({
      order_id: selectedOrderDetails.order_number,
      order_date: moment(selectedOrderDetails.created_on).format('MM/DD/YYYY'),
      rental_period: undefined,
      delivery_date: deliveryDate ? dayjs(deliveryDate) : null,
      pickup_date: pickupDate ? dayjs(pickupDate) : null,
      extension_type: 'weekly'
    });
    setExtensionType('weekly');
  };

  // Pricing functions
  const getRentalExtensionPricing = (productTitle) => {
    if (!productTitle) return null;

    const title = productTitle.toLowerCase();

    if (extensionPricingData?.box_packages) {
      for (const [key, value] of Object.entries(extensionPricingData.box_packages)) {
        if (title.includes(key.toLowerCase())) {
          return {
            weeklyRate: value.weekly_rate,
            dailyRate: value.daily_rate,
            packageType: value.item_identifier
          };
        }
      }
    }

    if (title.includes('studio')) {
      return { weeklyRate: 50, dailyRate: 10, packageType: 'Studio' };
    } else if (title.includes('1 bedroom') || title.includes('one bedroom')) {
      return { weeklyRate: 60, dailyRate: 15, packageType: '1 Bedroom' };
    } else if (title.includes('2 bedroom') || title.includes('two bedroom')) {
      return { weeklyRate: 70, dailyRate: 20, packageType: '2 Bedroom' };
    } else if (title.includes('3 bedroom') || title.includes('three bedroom')) {
      return { weeklyRate: 80, dailyRate: 25, packageType: '3 Bedroom' };
    } else if (title.includes('4 bedroom') || title.includes('four bedroom')) {
      return { weeklyRate: 100, dailyRate: 35, packageType: '4 Bedroom' };
    } else if (title.includes('5 bedroom') || title.includes('five bedroom')) {
      return { weeklyRate: 120, dailyRate: 40, packageType: '5 Bedroom' };
    }

    return { weeklyRate: 50, dailyRate: 10, packageType: 'Standard' };
  };

  const getMovingSuppliesPricing = (productTitle) => {
    if (!productTitle) return { weeklyRate: 0, dailyRate: 0 };

    const title = productTitle.toLowerCase();

    if (extensionPricingData?.moving_supplies) {
      for (const [key, value] of Object.entries(extensionPricingData.moving_supplies)) {
        if (title.includes(key.toLowerCase())) {
          return {
            weeklyRate: value.weekly_rate,
            dailyRate: value.daily_rate
          };
        }
      }
    }

    if (title.includes('moving dolly') || title.includes('custom dolly') ||
      title.includes('wardrobe box') || title.includes('big blue box')) {
      return { weeklyRate: 10, dailyRate: 5 };
    }

    return { weeklyRate: 0, dailyRate: 0 };
  };

  const getMainBoxPackage = (orderDetails) => {
    if (!orderDetails?.cart_items || orderDetails.cart_items.length === 0) return null;

    const boxPackage = orderDetails.cart_items.find(item =>
      item.sub_category === "Box Packages"
    );

    return boxPackage;
  };

  const getMovingSupplies = (orderDetails) => {
    if (!orderDetails?.cart_items || orderDetails.cart_items.length === 0) return [];

    const movingSupplies = orderDetails.cart_items.filter(item =>
      item.sub_category === "Moving Supplies"
    );

    return movingSupplies;
  };

  const getExistingRentalDuration = (orderDetails) => {
    if (!orderDetails?.cart_items || orderDetails.cart_items.length === 0) return 0;
    return orderDetails.cart_items[0]?.rental_duration || 0;
  };

  const calculateNewPickupDate = (currentPickupDate, extensionWeeks) => {
    if (!currentPickupDate) return null;

    const calculatedDate = dayjs(currentPickupDate).add(extensionWeeks * 7, 'days');

    // Check if calculated date is blocked and find next available date if needed
    if (isDateBlocked(calculatedDate)) {
      const nextAvailable = findNextAvailableDate(calculatedDate);
      return {
        date: nextAvailable,
        wasAdjusted: true,
        originalCalculated: calculatedDate
      };
    }

    return {
      date: calculatedDate,
      wasAdjusted: false,
      originalCalculated: null
    };
  };

  // New function to calculate daily extension days
  const calculateDailyExtensionDays = (originalPickupDate, newPickupDate) => {
    if (!originalPickupDate || !newPickupDate) return 0;

    const originalDate = dayjs(originalPickupDate);
    const newDate = dayjs(newPickupDate);

    return newDate.diff(originalDate, 'days');
  };

  // New function to generate daily pricing breakdown
  const generateDailyPricingBreakdown = (selectedOrderDetails, newPickupDate) => {
    if (!selectedOrderDetails?.cart_items || selectedOrderDetails.cart_items.length === 0 || !newPickupDate) {
      return null;
    }

    const mainBoxPackage = getMainBoxPackage(selectedOrderDetails);
    const movingSupplies = getMovingSupplies(selectedOrderDetails);
    const originalPickupDate = selectedOrderDetails.pickup_date;

    if (!mainBoxPackage || !originalPickupDate) return null;

    const extensionDays = calculateDailyExtensionDays(originalPickupDate, newPickupDate);

    if (extensionDays <= 0) return null;

    const productTitle = mainBoxPackage.product_title || mainBoxPackage.product_name;
    const pricing = getRentalExtensionPricing(productTitle);

    if (!pricing) return null;

    let totalMovingSuppliesDailyRate = 0;
    const movingSuppliesBreakdown = [];

    movingSupplies.forEach(item => {
      const movingPricing = getMovingSuppliesPricing(item.product_title || item.product_name);
      const itemDailyRate = movingPricing.dailyRate * item.quantity;
      totalMovingSuppliesDailyRate += itemDailyRate;

      if (itemDailyRate > 0) {
        movingSuppliesBreakdown.push({
          name: item.product_title || item.product_name,
          quantity: item.quantity,
          dailyRate: movingPricing.dailyRate,
          totalDailyRate: itemDailyRate,
          totalPrice: itemDailyRate * extensionDays
        });
      }
    });

    const boxPackagePrice = pricing.dailyRate * extensionDays;
    const movingSuppliesPrice = totalMovingSuppliesDailyRate * extensionDays;
    const totalPrice = boxPackagePrice + movingSuppliesPrice;

    return {
      extensionDays,
      boxPackagePrice,
      movingSuppliesPrice,
      totalPrice,
      movingSuppliesBreakdown,
      boxPackageDailyRate: pricing.dailyRate,
      movingSuppliesDailyRate: totalMovingSuppliesDailyRate,
      packageType: pricing.packageType
    };
  };

  const generateWeeklyPricingOptions = (selectedOrderDetails) => {
    if (!selectedOrderDetails?.cart_items || selectedOrderDetails.cart_items.length === 0) {
      return [];
    }

    const mainBoxPackage = getMainBoxPackage(selectedOrderDetails);
    const movingSupplies = getMovingSupplies(selectedOrderDetails);

    if (!mainBoxPackage) return [];

    const productTitle = mainBoxPackage.product_title || mainBoxPackage.product_name;
    const pricing = getRentalExtensionPricing(productTitle);

    if (!pricing) return [];

    let totalMovingSuppliesWeeklyRate = 0;

    movingSupplies.forEach(item => {
      const movingPricing = getMovingSuppliesPricing(item.product_title || item.product_name);
      totalMovingSuppliesWeeklyRate += movingPricing.weeklyRate * item.quantity;
    });

    const combinedWeeklyRate = pricing.weeklyRate + totalMovingSuppliesWeeklyRate;
    const weekOptions = [1, 2, 3, 4, 5];

    const allOptions = weekOptions.map(weeks => {
      return {
        value: `${weeks}_week${weeks > 1 ? 's' : ''}`,
        label: `${weeks} week${weeks > 1 ? 's' : ''}`,
        price: combinedWeeklyRate * weeks,
        weeks: weeks,
        boxPackagePrice: pricing.weeklyRate * weeks,
        movingSuppliesPrice: totalMovingSuppliesWeeklyRate * weeks,
        boxPackageWeeklyRate: pricing.weeklyRate,
        movingSuppliesWeeklyRate: totalMovingSuppliesWeeklyRate
      };
    });

    return allOptions;
  };

  const handleExtensionTypeChange = (e) => {
    const newType = e.target.value;
    setExtensionType(newType);
    form.setFieldsValue({ extension_type: newType });

    // Reset dependent fields
    if (newType === 'daily') {
      form.setFieldsValue({ rental_period: undefined });
    } else {
      form.setFieldsValue({ pickup_date: null });
    }
  };

  const handleRentalPeriodChange = (value) => {
    if (!value || !selectedOrderDetails) return;

    const pricingOptions = generateWeeklyPricingOptions(selectedOrderDetails);
    const selectedOption = pricingOptions.find(option => option.value === value);

    if (selectedOption) {
      const currentPickupDate = selectedOrderDetails.pickup_date;
      const pickupResult = calculateNewPickupDate(currentPickupDate, selectedOption.weeks);

      if (pickupResult) {
        // Update the state based on whether date was adjusted
        setShowDateAdjustmentMessage(pickupResult.wasAdjusted);
        setOriginalCalculatedDate(pickupResult.originalCalculated);

        form.setFieldsValue({
          pickup_date: pickupResult.date
        });
      }
    }
  };

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      const formValues = await form.validateFields();

      if (extensionType === 'weekly' && !formValues.rental_period) {
        message.error('Please select a rental period for weekly extension.');
        return;
      }

      if (extensionType === 'daily' && !formValues.pickup_date) {
        message.error('Please select a pickup date for daily extension.');
        return;
      }

      let extensionPrice = 0;
      let boxPackagePrice = 0;
      let movingSuppliesPrice = 0;
      let selectedWeeks = 0;
      let selectedDays = 0;
      let finalPickupDate = null;
      let intendedPickupDate = null; // Track intended date before blocked date adjustment

      if (extensionType === 'weekly' && formValues.rental_period) {
        const pricingOptions = generateWeeklyPricingOptions(selectedOrderDetails);
        const selectedOption = pricingOptions.find(option => option.value === formValues.rental_period);
        if (selectedOption) {
          extensionPrice = selectedOption.price;
          boxPackagePrice = selectedOption.boxPackagePrice;
          movingSuppliesPrice = selectedOption.movingSuppliesPrice;
          selectedWeeks = selectedOption.weeks;

          // Calculate the new pickup date for weekly extension
          const currentPickupDate = selectedOrderDetails.pickup_date;
          const pickupResult = calculateNewPickupDate(currentPickupDate, selectedOption.weeks);

          if (pickupResult) {
            finalPickupDate = pickupResult.date;
            // Store the intended date (before blocked date adjustment)
            intendedPickupDate = pickupResult.originalCalculated || pickupResult.date;
          }
        }
      } else if (extensionType === 'daily' && formValues.pickup_date) {
        const dailyBreakdown = generateDailyPricingBreakdown(selectedOrderDetails, formValues.pickup_date);
        if (dailyBreakdown) {
          extensionPrice = dailyBreakdown.totalPrice;
          boxPackagePrice = dailyBreakdown.boxPackagePrice;
          movingSuppliesPrice = dailyBreakdown.movingSuppliesPrice;
          selectedDays = dailyBreakdown.extensionDays;
          finalPickupDate = formValues.pickup_date;

          // For daily extension, the user selected date is the intended date
          // (no automatic blocked date adjustment in daily mode)
          intendedPickupDate = formValues.pickup_date;
        }
      }

      const mainBoxPackage = getMainBoxPackage(selectedOrderDetails);
      const movingSupplies = getMovingSupplies(selectedOrderDetails);

      const formattedPickupDate = finalPickupDate ? finalPickupDate.format('MM/DD/YYYY') : '';
      const formattedIntendedPickupDate = intendedPickupDate ? intendedPickupDate.format('MM/DD/YYYY') : '';
      const displayPickupDate = finalPickupDate ? finalPickupDate.format('MMM DD, YYYY (dddd)') : 'N/A';

      const summaryData = {
        boxPackage: {
          name: extensionType === 'weekly'
            ? `${mainBoxPackage?.product_title || mainBoxPackage?.product_name} (${selectedWeeks} Week${selectedWeeks > 1 ? 's' : ''} Extension)`
            : `${mainBoxPackage?.product_title || mainBoxPackage?.product_name} (${selectedDays} Day${selectedDays > 1 ? 's' : ''} Extension)`,
          price: boxPackagePrice || 0
        },
        movingSupplies: movingSupplies.map(item => ({
          name: item.product_title || item.product_name,
          price: extensionType === 'weekly'
            ? getMovingSuppliesPricing(item.product_title || item.product_name).weeklyRate * item.quantity * selectedWeeks
            : getMovingSuppliesPricing(item.product_title || item.product_name).dailyRate * item.quantity * selectedDays
        })),
        pickupDate: displayPickupDate,
        newPickupDate: displayPickupDate,
        totalCost: extensionPrice || 0,
        extensionWeeks: selectedWeeks || 0,
        extensionDays: selectedDays || 0,
        extensionType: extensionType,
        orderDetails: selectedOrderDetails,
        requestData: {
          rental_period: formValues.rental_period || null,
          extension_type: extensionType,
          total_extension_price: extensionPrice || 0,
          box_package_price: boxPackagePrice || 0,
          moving_supplies_price: movingSuppliesPrice || 0,
          pickup_date: formattedPickupDate,
          intended_pickup_date: formattedIntendedPickupDate, // Add intended date for success message
          delivery_date: formValues.delivery_date ? formValues.delivery_date.format('MM/DD/YYYY') : null,
          existing_rental_weeks: getExistingRentalDuration(selectedOrderDetails),
          extension_days: selectedDays || 0,
          extension_weeks: selectedWeeks || 0
        }
      };

      console.log('=== DEBUG: Summary Data ===');
      console.log('Summary Data:', summaryData);
      console.log('Request Data pickup_date:', summaryData.requestData.pickup_date);

      onOpenSummary(summaryData);

    } catch (error) {
      console.error('Error preparing rental extension request:', error);
      if (error.errorFields) {
        message.error('Please fill in all required fields correctly.');
      } else {
        message.error('Failed to prepare rental extension request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
    form.resetFields();
    setExtensionType('weekly');
    setShowDateAdjustmentMessage(false);
    setOriginalCalculatedDate(null);
  };

  if (!selectedOrder) return null;

  return (
    <Modal
      footer={false}
      centered
      title={
        <div className="d-flex justify-content-between align-items-center w-100 modal-custom-title p-2 rounded-t-xl text-white">
          <span></span>
          <span>Extend Rental Period</span>
          <CloseOutlined
            className="date-change-modal modal-custom"
            onClick={handleCancel}
          />
        </div>
      }
      closable={false}
      open={open}
      width={800}
      className="date-change-modal modal-custom"
    >
      {(isSubmitting || loadingOrderDetails) && (
        <div className="extend-rental-loading-overlay">
          <div className="extend-rental-loading-content">
            <Spin size="large" />
            <div className="extend-rental-loading-text">
              {loadingOrderDetails ? 'Loading order details...' : 'Processing your request...'}
            </div>
          </div>
        </div>
      )}

      {selectedOrderDetails && !loadingOrderDetails && (
        <div className="extend-rental-order-details">
          <h3 className="extend-rental-order-details-title">Order Details:</h3>

          <div className="extend-rental-order-info mb-2">
            <Row gutter={[16, 8]}>
              <Col span={6}>
                <div className='d-flex order-details flex-column'><strong>Order ID:</strong> {selectedOrderDetails.order_number}</div>
              </Col>
              <Col span={6}>
                <div className='d-flex order-details flex-column'><strong>Order Date:</strong> {moment(selectedOrderDetails.order_date).format('MM/DD/YYYY')}</div>
              </Col>
              <Col span={6}>
                <div className='d-flex order-details flex-column'><strong>Delivery Date:</strong> {moment(selectedOrderDetails.delivery_date).format('MM/DD/YYYY')}</div>
              </Col>
              <Col span={6}>
                <div className='d-flex order-details flex-column'><strong>Pickup Date:</strong> {moment(selectedOrderDetails.pickup_date).format('MM/DD/YYYY')}</div>
              </Col>
            </Row>
          </div>

          {(() => {
            const mainBoxPackage = selectedOrderDetails.cart_items?.find(item =>
              item.sub_category === "Box Packages"
            );
            const movingSupplies = selectedOrderDetails.cart_items?.filter(item =>
              item.sub_category === "Moving Supplies"
            ) || [];

            return mainBoxPackage ? (
              <div className="extend-rental-box-package">
                <div className="extend-rental-box-package-title">
                  Box Package:
                </div>
                <Row gutter={16}>
                  <Col span={6}>
                    <div>Package: {mainBoxPackage.product_title || mainBoxPackage.product_name}</div>
                  </Col>
                  <Col span={6}>
                    <div>Quantity: {mainBoxPackage.quantity}</div>
                  </Col>
                  <Col span={6}>
                    <div>Rental Period: {mainBoxPackage.rental_duration} {mainBoxPackage.rental_duration === 1 ? 'week' : 'weeks'}</div>
                  </Col>
                  <Col span={6}>
                    <div>Package Price: ${mainBoxPackage.cart_price}</div>
                  </Col>
                </Row>

                {movingSupplies.length > 0 && (
                  <div className="extend-rental-moving-supplies">
                    <div className="extend-rental-moving-supplies-title">
                      Included Moving Supplies:
                    </div>
                    {movingSupplies.map((supply, index) => (
                      <div key={index} className="extend-rental-supply-item">
                        <Row gutter={16}>
                          <Col span={8}>
                            <div className="extend-rental-supply-item-text">Item: {supply.product_title || supply.product_name}</div>
                          </Col>
                          <Col span={4}>
                            <div className="extend-rental-supply-item-text">Qty: {supply.quantity}</div>
                          </Col>
                          <Col span={6}>
                            <div className="extend-rental-supply-item-text">Duration: {supply.rental_duration} {supply.rental_duration === 1 ? 'week' : 'weeks'}</div>
                          </Col>
                          <Col span={6}>
                            <div className="extend-rental-supply-item-text">Price: ${supply.cart_price}</div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null;
          })()}
        </div>
      )}

      <Form form={form} layout="vertical" autoComplete="off" className="extend-rental-form">
        {/* Extension Type Selection and Pickup Date in one line */}
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Form.Item name="extension_type" style={{ marginBottom: '0' }} className="extend-rental-form-item">
              <Radio.Group
                value={extensionType}
                onChange={handleExtensionTypeChange}
                disabled={isSubmitting || loadingOrderDetails}
              >
                <Radio value="daily">Daily</Radio>
                <Radio value="weekly">Weekly</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={18}>
            <Form.Item
              name={extensionType === 'weekly' ? 'rental_period' : 'pickup_date'}
              label="Pickup date"
              className="extend-rental-form-item"
              rules={[
                { required: true, message: extensionType === 'weekly' ? 'Please select rental period extension' : 'Please select new pickup date' },
                ...(extensionType === 'daily' ? [({ getFieldValue }) => ({
                  validator(_, value) {
                    const deliveryDate = getFieldValue('delivery_date');
                    if (!value || !deliveryDate) {
                      return Promise.resolve();
                    }
                    if (value.isBefore(deliveryDate, 'day')) {
                      return Promise.reject(new Error('New pickup date cannot be before delivery date'));
                    }
                    return Promise.resolve();
                  },
                })] : [])
              ]}
            >
              {extensionType === 'weekly' ? (
                <Select
                  size="large"
                  placeholder="select week"
                  disabled={isSubmitting || loadingOrderDetails}
                  onChange={handleRentalPeriodChange}
                  allowClear
                >
                  {generateWeeklyPricingOptions(selectedOrderDetails).map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                <DatePicker
                  size="large"
                  style={{ width: '100%' }}
                  format="MM/DD/YYYY"
                  disabled={isSubmitting || loadingOrderDetails}
                  allowClear={true}
                  showToday={true}
                  placeholder="select date"
                  disabledDate={(current) => {
                    if (!current) return false;

                    const deliveryDate = form.getFieldValue('delivery_date');
                    const currentPickupDate = selectedOrderDetails?.pickup_date;
                    const today = dayjs();

                    // Disable past dates
                    if (current.isBefore(today, 'day')) return true;

                    // Disable dates before delivery date
                    if (deliveryDate && current.isBefore(deliveryDate, 'day')) return true;

                    // Disable dates before current pickup date
                    if (currentPickupDate && current.isBefore(dayjs(currentPickupDate), 'day')) return true;

                    // Disable dates within 48 hours
                    const hoursDifference = current.diff(today, 'hours');
                    if (hoursDifference < 48) return true;

                    // Disable blocked weekdays (from admin)
                    const currentDayName = current.format('dddd');
                    if (blockedDays.includes(currentDayName)) return true;

                    // Disable specific unavailable dates (from admin)
                    const currentDateString = current.format('YYYY-MM-DD');
                    if (unavailableDates.some(dateObj => dayjs(dateObj.date).format('YYYY-MM-DD') === currentDateString)) return true;

                    return false;
                  }}
                />
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* Extension Details Display */}
        {extensionType === 'weekly' && (
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.rental_period !== currentValues.rental_period}>
            {({ getFieldValue }) => {
              const rentalPeriodValue = getFieldValue('rental_period');
              if (!rentalPeriodValue || !selectedOrderDetails) return null;

              const pricingOptions = generateWeeklyPricingOptions(selectedOrderDetails);
              const selectedOption = pricingOptions.find(option => option.value === rentalPeriodValue);

              if (selectedOption) {
                const currentPickupDate = selectedOrderDetails.pickup_date;
                const pickupResult = calculateNewPickupDate(currentPickupDate, selectedOption.weeks);
                const newPickupDate = pickupResult ? pickupResult.date : null;

                // Get the weekly rates for display
                const mainBoxPackage = getMainBoxPackage(selectedOrderDetails);
                const productTitle = mainBoxPackage?.product_title || mainBoxPackage?.product_name;
                const pricing = getRentalExtensionPricing(productTitle);
                const weeklyRate = pricing?.weeklyRate || 0;

                return (
                  <div className="extend-rental-extension-info">
                    <div className="extend-rental-extension-breakdown">
                      <strong>Extension Charges Breakdown:</strong>
                    </div>
                    <div className="extend-rental-extension-details">
                      <div>Box Package: <strong>${weeklyRate}/week - ${selectedOption.boxPackagePrice} for {selectedOption.weeks} {selectedOption.weeks === 1 ? 'week' : 'weeks'}</strong></div>
                      {selectedOption.movingSuppliesPrice > 0 && (() => {
                        const movingSupplies = getMovingSupplies(selectedOrderDetails);
                        return movingSupplies.map((supply, index) => {
                          const supplyPricing = getMovingSuppliesPricing(supply.product_title || supply.product_name);
                          const supplyWeeklyRate = supplyPricing.weeklyRate * supply.quantity;
                          const supplyTotalPrice = supplyWeeklyRate * selectedOption.weeks;
                          return (
                            <div key={index}>
                              {supply.product_title || supply.product_name}: <strong>${supplyWeeklyRate}/week - ${supplyTotalPrice} for {selectedOption.weeks} {selectedOption.weeks === 1 ? 'week' : 'weeks'}</strong>
                            </div>
                          );
                        });
                      })()}
                      <div className="extend-rental-extension-total">
                        Total Amount: <strong>${selectedOption.price} for {selectedOption.weeks} {selectedOption.weeks === 1 ? 'week' : 'weeks'}</strong>
                      </div>
                    </div>
                    <div className="extend-rental-extension-note">
                      The pickup date will be automatically updated based on your selected extension period.
                    </div>
                    <div className="extend-rental-new-pickup">
                      <strong>New Pickup Date:</strong> {newPickupDate ? newPickupDate.format('MM/DD/YYYY') : 'N/A'}
                    </div>

                    {/* Date adjustment notification */}
                    {showDateAdjustmentMessage && originalCalculatedDate && (
                      <div style={{
                        backgroundColor: '#e6f4ff',
                        border: '1px solid #91caff',
                        borderRadius: '8px',
                        padding: '12px 14px',
                        marginTop: '12px',
                        color: '#0050b3'
                      }}>
                        <p className="mb-0" style={{ fontSize: '12px', lineHeight: '1.3' }}>
                          <strong>Note:</strong> Your calculated pickup date ({originalCalculatedDate.format('dddd, MMMM DD, YYYY')}) was not available.
                          We have automatically assigned the next available date. You will not be charged for any additional days added to your rental extension request.
                        </p>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            }}
          </Form.Item>
        )}

        {/* Daily Extension Details Display */}
        {extensionType === 'daily' && (
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.pickup_date !== currentValues.pickup_date}>
            {({ getFieldValue }) => {
              const pickupDateValue = getFieldValue('pickup_date');
              if (!pickupDateValue || !selectedOrderDetails) return null;

              const dailyBreakdown = generateDailyPricingBreakdown(selectedOrderDetails, pickupDateValue);

              if (dailyBreakdown && dailyBreakdown.extensionDays > 0) {
                return (
                  <div className="extend-rental-extension-info">
                    <div className="extend-rental-extension-breakdown">
                      <strong>Extension Charges Breakdown:</strong>
                    </div>
                    <div className="extend-rental-extension-details">
                      <div>Box Package: <strong>${dailyBreakdown.boxPackageDailyRate}/day - ${dailyBreakdown.boxPackagePrice} for {dailyBreakdown.extensionDays} {dailyBreakdown.extensionDays === 1 ? 'day' : 'days'}</strong></div>
                      {dailyBreakdown.movingSuppliesPrice > 0 && dailyBreakdown.movingSuppliesBreakdown.map((supply, index) => (
                        <div key={index}>
                          {supply.name}: <strong>${supply.totalDailyRate}/day - ${supply.totalPrice} for {dailyBreakdown.extensionDays} {dailyBreakdown.extensionDays === 1 ? 'day' : 'days'}</strong>
                        </div>
                      ))}
                      <div className="extend-rental-extension-total">
                        Total Amount: <strong>${dailyBreakdown.totalPrice} for {dailyBreakdown.extensionDays} {dailyBreakdown.extensionDays === 1 ? 'day' : 'days'}</strong>
                      </div>
                    </div>
                    <div className="extend-rental-extension-note">
                      Extension period calculated from original pickup date ({moment(selectedOrderDetails.pickup_date).format('MM/DD/YYYY')}) to selected date.
                    </div>
                    <div className="extend-rental-new-pickup">
                      <strong>New Pickup Date:</strong> {pickupDateValue.format('MM/DD/YYYY')}
                    </div>

                    {/* Date adjustment notification for daily extensions */}
                    {showDateAdjustmentMessage && originalCalculatedDate && (
                      <div style={{
                        backgroundColor: '#e6f4ff',
                        border: '1px solid #91caff',
                        borderRadius: '8px',
                        padding: '12px 14px',
                        marginTop: '12px',
                        color: '#0050b3'
                      }}>
                        <p className="mb-0" style={{ fontSize: '12px', lineHeight: '1.3' }}>
                          <strong>Note:</strong> Your selected pickup date ({originalCalculatedDate.format('dddd, MMMM DD, YYYY')}) was not available.
                          We have automatically assigned the next available date. You will not be charged for any additional days added to your original rental duration.
                        </p>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            }}
          </Form.Item>
        )}

        <Row>
          <Col span={24}>
            <div className="extend-rental-submit-container">
              <Button
                size="large"
                type="primary"
                className='signup-register-btn'
                onClick={onSubmit}
                loading={isSubmitting}
                disabled={isSubmitting || loadingOrderDetails}
              >
                {isSubmitting ? 'Processing...' : 'Submit & Review'}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

ExtendRentalModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedOrder: PropTypes.object,
  selectedOrderDetails: PropTypes.object,
  loadingOrderDetails: PropTypes.bool,
  customer: PropTypes.object,
  onOpenSummary: PropTypes.func.isRequired
};

export default ExtendRentalModal;